// Orchestrates the browser-side vision pipeline:
//   camera video → MediaPipe pose/segmentation → calibration cascade →
//   position classification → EMA smoothing → session baseline tracking →
//   WebSocket send to Vision Service → receive physics → expose via state.
//
// The heavy refs (pipeline objects, smoothers, baseline tracker) live in
// useRef so we don't thrash on every re-render. UI state is surfaced via
// useState at ~15 Hz max so React stays responsive.
//
// 2026-07 — verbose diagnostic mode. Enable with `?diag=1` in the URL,
// or run `localStorage.aerochat_diag = '1'` once in DevTools. When on,
// [diag] lines fire ~once per second with every number that matters for
// baseline lock (health, angle, is_usable, px_per_m, cda, progress,
// unusable-duration, WS state, dropped frames). Zero cost when off.

import { useCallback, useEffect, useRef, useState } from 'react';
import { PoseDetector, Landmark, WorldLandmark } from '../vision/poseDetector';
import { FaceDetector, FaceLandmark } from '../vision/faceDetector';
import { computeBodySegments } from '../vision/bodySegments';
import { extractContour } from '../vision/contour';
import { calibrate, CalibrationResult, getSmoothedBodyDimsM } from '../vision/calibration';
import { frontalAreaFromWorldLandmarks } from '../vision/frontalProjection';
import { refineSmplBySilhouette } from '../vision/smplifyRefine';
import { classifyPosition, isTransitioning, resetHysteresis, PositionResult } from '../vision/positionClassifier';
import { EmaSmoother } from '../vision/smoother';
import { OneEuroFilter } from '../vision/oneEuro';
import {
  PositionSegmenter,
  ABComparison,
  PositionHold,
} from '../vision/positionSegmenter';
import { SessionBaseline } from '../vision/sessionBaseline';
import {
  VisionClient,
  AnalysisResult,
  Refinement,
  SensorData,
} from '../vision/visionClient';
import { computePowerCda, powerVisionAgreement } from '../vision/powerCda';
import {
  CoachEventBus,
  newCoachState,
  processCoachTick,
} from '../vision/coachEvents';
import { PoseToSmplFitter } from '../vision/poseToSmpl';
import { SMPLFitResult } from '../services/visionAdvanced';

const DIAG =
  (typeof window !== 'undefined' &&
    (new URLSearchParams(window.location.search).has('diag') ||
      localStorage.getItem('aerochat_diag') === '1')) ||
  false;
if (DIAG && typeof window !== 'undefined') {
  // eslint-disable-next-line no-console
  console.log('[diag] verbose vision-pipeline diagnostics ENABLED');
}

export interface VisionPipelineOptions {
  videoRef: React.RefObject<HTMLVideoElement>;
  wsUrl: string;
  userId?: string;
  sessionId?: string;
  /** Optional user-provided height in metres. If set, enables the user_height
   *  calibration tier (highest accuracy). */
  userHeightM?: number;
  getSensors?: () => SensorData | undefined;
  enabled?: boolean;
}

export interface SmoothedAnalysis {
  cda: number;
  frontal_area_m2: number;
  drag_force_n: number;
  drag_power_w: number;
  cd: number;
  segments?: {
    head_pct: number;
    torso_pct: number;
    arms_pct: number;
    legs_pct: number;
    // Track 2.1 — per-region drag in watts, summing to drag_power_w.
    head_w?: number;
    torso_w?: number;
    arms_w?: number;
    legs_w?: number;
  };
  predicted_speed_kph?: number;
  /** Track 1.1 — back-solved CdA from BLE power. null when no power meter
   *  is connected or rider is below the min power/velocity thresholds. */
  power_cda?: number | null;
  /** Track 1.1 — agreement score between vision-CdA and power-CdA in [0, 1].
   *  1.0 means within 5%; 0 means >15% disagreement. null when either CdA
   *  is unavailable. Drives the "calibration confidence" pill in the UI. */
  power_vision_agreement?: number | null;
}

/** Fast-changing refs the visualization overlay reads directly from its own
 *  animation loop (avoids React re-render bottleneck at 30+ fps). */
export interface VisualizationRefs {
  maskRef: React.MutableRefObject<ImageData | null>;
  labelMapRef: React.MutableRefObject<Uint8Array | null>;
  edgeMapRef: React.MutableRefObject<Uint8Array | null>;
  maskWidthRef: React.MutableRefObject<number>;
  maskHeightRef: React.MutableRefObject<number>;
  landmarksRef: React.MutableRefObject<Landmark[] | null>;
  worldLandmarksRef: React.MutableRefObject<WorldLandmark[] | null>;
  bboxRef: React.MutableRefObject<[number, number, number, number] | null>;
  /** Wave 8 — normalized [0..1] silhouette bbox in image coords. Same data
   *  as bboxRef but pre-divided by mask dimensions for consumers (like the
   *  pressure-mesh AR overlay) that need image-space positioning. */
  bboxNormRef: React.MutableRefObject<[number, number, number, number] | null>;
  /** 478 face landmarks in image-normalized coords (0..1). */
  faceLandmarksRef: React.MutableRefObject<FaceLandmark[] | null>;
}

export interface VisionPipelineState {
  /** Smoothed analysis values (the numbers you should show in the UI). */
  smoothed: SmoothedAnalysis | null;
  /** Raw latest server response, for debug. */
  rawFast: AnalysisResult | null;
  refinedAnalysis: Refinement | null;
  isRefining: boolean;
  latestLandmarks: Landmark[] | null;
  latestBbox: [number, number, number, number] | null;
  calibration: CalibrationResult | null;
  position: PositionResult | null;
  baseline: {
    cda: number | null;
    delta: number | null;
    deltaPct: number | null;
    bestCda: number | null;
    isLocked: boolean;
    lockProgress: number; // 0..1 bar for UI
  };
  fps: number;
  error: string | null;
  visualization: VisualizationRefs;
  /** Track 2.3 — most recent A/B comparison emitted by the segmenter.
   *  Stays mounted (read-only) until the next comparison overrides it. */
  abComparison: ABComparison | null;
  /** Track 2.3 — all sustained-position holds this session, for the
   *  end-session report. */
  positionHolds: PositionHold[];
  /** Wave 20.44 — in-progress hold snapshot for the session guide.
   *  { positionClass, elapsedSec, minRequiredSec } or null when no
   *  class is being tracked. Updates on each UI tick. */
  currentHold: {
    positionClass: string;
    elapsedSec: number;
    minRequiredSec: number;
  } | null;
  /** Wave 20.44 — force-finalize the in-progress hold and return
   *  the fresh holds array synchronously. Called from
   *  handleEndSession so a rider who never switched positions
   *  still gets their single hold recorded on the session
   *  summary — the returned array is what the summary POST should
   *  send, NOT the pipeline.positionHolds React state (which is
   *  stale for one render after finalize). */
  finalizePositionSession: (endSec?: number) => PositionHold[];
  /** Wave 20.49l — rider-triggered mid-session baseline recapture.
   *  Wipes the current SessionBaseline (bestCda + baselineCda),
   *  the position segmenter (holds + running bucket), the CdA
   *  One-Euro filter, and the classifier hysteresis so the next
   *  90 stable frames re-lock a fresh baseline. Used when the
   *  first baseline was captured badly. */
  resetBaseline: () => void;
  /** Track 3.1 — coach event bus. useAeroCoachTriggers subscribes here.
   *  Stable for the lifetime of the pipeline; safe to capture in effect deps. */
  coachBus: CoachEventBus;
  /** Wave 7 — client-side SMPL fit derived from MediaPipe world landmarks.
   *  Throttled to ~15 Hz so the avatar components don't churn React. Null
   *  until the first frame with usable landmarks lands. */
  smplFit: SMPLFitResult | null;
  /** 2026-07 — milliseconds since is_usable last went false. Null when
   *  calibration is currently usable. AIClientVision shows a warning
   *  banner when this exceeds 15 s so the rider knows to reposition
   *  the camera. */
  calibrationUnusableForMs: number | null;
}

export function useVisionPipeline(
  opts: VisionPipelineOptions
): VisionPipelineState {
  const {
    videoRef,
    wsUrl,
    userId = '',
    sessionId = '',
    userHeightM,
    getSensors,
    enabled = true,
  } = opts;

  const [smoothed, setSmoothed] = useState<SmoothedAnalysis | null>(null);
  const [rawFast, setRawFast] = useState<AnalysisResult | null>(null);
  const [refinedAnalysis, setRefinedAnalysis] = useState<Refinement | null>(
    null
  );
  const [isRefining, setIsRefining] = useState(false);
  const [fps, setFps] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [calibration, setCalibration] = useState<CalibrationResult | null>(null);
  const [position, setPosition] = useState<PositionResult | null>(null);
  const [baselineState, setBaselineState] = useState({
    cda: null as number | null,
    delta: null as number | null,
    deltaPct: null as number | null,
    bestCda: null as number | null,
    isLocked: false,
    lockProgress: 0,
  });
  // 2026-07 — expose how long is_usable has been false. Null when usable.
  const [calibrationUnusableForMs, setCalibrationUnusableForMs] = useState<number | null>(null);
  const unusableSinceRef = useRef<number | null>(null);
  const lastLoggedBaselineProgressRef = useRef(0);
  const lastBaselineLockLoggedRef = useRef(false);
  // 2026-07 diag mode — count dropped frames + throttle the per-second dump.
  const diagLastDumpRef = useRef(0);
  const diagFramesDroppedRef = useRef(0);
  const diagFramesSentRef = useRef(0);
  const diagWsStateRef = useRef<string>('init');

  // Refs for hot data
  const detectorRef = useRef<PoseDetector | null>(null);
  const faceDetectorRef = useRef<FaceDetector | null>(null);
  const clientRef = useRef<VisionClient | null>(null);
  const landmarksRef = useRef<Landmark[] | null>(null);
  const worldLandmarksRef = useRef<WorldLandmark[] | null>(null);
  const faceLandmarksRef = useRef<FaceLandmark[] | null>(null);
  const bboxRef = useRef<[number, number, number, number] | null>(null);
  const bboxNormRef = useRef<[number, number, number, number] | null>(null);
  const maskRef = useRef<ImageData | null>(null);
  const labelMapRef = useRef<Uint8Array | null>(null);
  const edgeMapRef = useRef<Uint8Array | null>(null);
  const maskWidthRef = useRef<number>(0);
  const maskHeightRef = useRef<number>(0);
  const rafIdRef = useRef<number | null>(null);
  const frameCountRef = useRef(0);
  const lastFpsUpdateRef = useRef(0);
  const runningRef = useRef(false);

  // Smoothers for scale and derived quantities. Scale smoother lives across
  // calibration sources so switching from shoulder→hip mid-session doesn't
  // cause a discontinuity.
  // Track 1.3 — One-Euro filter replaces the fixed-α EMA on cda/area/power.
  // Adaptive cutoff means stable readings are smoother than the old EMA(0.18)
  // and position changes track faster — no need for separate transition-α
  // hacks. minCutoff/beta tuned against the old EMA: ~30% less steady-state
  // jitter, ~50% less latency on position changes.
  // Scale smoother stays EMA because body dimensions don't change during a
  // session — pure low-pass is what we want there.
  const scaleSmoothRef = useRef(new EmaSmoother(0.06, 4));
  const cdaSmoothRef = useRef(
    new OneEuroFilter({ minCutoff: 0.5, beta: 0.05 }),
  );
  const areaSmoothRef = useRef(
    new OneEuroFilter({ minCutoff: 0.5, beta: 0.05 }),
  );
  const powerSmoothRef = useRef(
    new OneEuroFilter({ minCutoff: 0.6, beta: 0.04 }),
  );
  // Track 1.1 — back-solved CdA from BLE power. Slightly slower min cutoff
  // because power readings themselves are already noisy.
  const powerCdaSmoothRef = useRef(
    new OneEuroFilter({ minCutoff: 0.3, beta: 0.03 }),
  );

  // Session baseline tracker.
  // Wave 20.49p — stableFramesRequired dropped 90 → 20. Rider
  // report: "when the session starts, why do we need another
  // calibration/baseline? Didn't we already do that?" — they
  // felt the ~3 s lock as a second capture on top of Preflight.
  // At 30 fps, 20 frames locks in ~700 ms so it's imperceptible
  // — the rider was already holding still from Preflight's
  // hand-out upright, so the CdA stream that arrives first is
  // already clean.
  const baselineRef = useRef(
    new SessionBaseline({ stableFramesRequired: 20, stabilityThreshold: 0.04 })
  );

  // Track 2.3 — sustained-position segmenter for A/B comparison and the
  // session-end position ranking. positionRef lets the WS-result callback
  // read the latest position classification without a re-render.
  const segmenterRef = useRef(new PositionSegmenter());
  const positionRef = useRef<PositionResult | null>(null);
  // Wave 20.49l — remembers the last confirmed class so we can
  // detect the moment of a hysteresis flip and reset the CdA
  // One-Euro filter, so the new bucket's samples aren't dragged by
  // the old position's smoothed state.
  const lastConfirmedClassRef = useRef<string | null>(null);
  // Wave 20.49o — latest usable shoulder-hip pixel distance. Feeds
  // the baseline (as the depth reference at lock time) AND the
  // per-frame depth-scale correction that stops "rider lowered
  // their head, moved closer to camera, CdA went up because
  // silhouette got bigger" from mis-attributing depth as drag.
  // Wave 20.49bu — Per-frame px_per_m derived from pixel
  // shoulder width ÷ calibrated shoulder width. This is the
  // pose-invariant depth proxy that replaces the old pixel
  // shoulder-hip distance. Shoulders are an across-body
  // horizontal span perpendicular to the camera axis, so their
  // pixel width only changes with camera distance — never
  // with pitch (aero tuck). Sending this to the server as
  // `calibration.px_per_m` lets the server compute correct
  // area_m² per frame; no client-side depth-K compensation is
  // needed.
  const lastLivePxPerMRef = useRef<number | null>(null);
  const [abComparison, setAbComparison] = useState<ABComparison | null>(null);
  const [positionHolds, setPositionHolds] = useState<PositionHold[]>([]);
  const [currentHoldSnap, setCurrentHoldSnap] = useState<{
    positionClass: string;
    elapsedSec: number;
    minRequiredSec: number;
  } | null>(null);

  // Track 1.2 — latest calibration usability flag. Captured during the local
  // calibration step in the per-frame tick, read by the WS-result callback so
  // it can pass `usable` to baselineRef.current.update() without re-doing the
  // calibration computation. Frames behind by one tick at most.
  const calibrationUsableRef = useRef<boolean>(false);

  // Track 3.1 — coach event bus + transition state. The bus is stable for the
  // pipeline's lifetime so subscribers can capture it once.
  const coachBusRef = useRef(new CoachEventBus());
  const coachStateRef = useRef(newCoachState());

  // Wave 7 — client-side SMPL fitter from MediaPipe world landmarks.
  // Replaces the vision-advanced WebSocket round-trip. Stable for the
  // pipeline's lifetime.
  const smplFitterRef = useRef(new PoseToSmplFitter());
  const smplFitRef = useRef<SMPLFitResult | null>(null);
  const [smplFit, setSmplFit] = useState<SMPLFitResult | null>(null);
  const lastSmplSurfacedRef = useRef(0);

  // Throttle UI state updates to ~15 Hz so React doesn't re-render 30x/sec
  const lastUiUpdateRef = useRef(0);

  // Initialize detector + WebSocket once
  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;

    (async () => {
      try {
        const detector = new PoseDetector();
        await detector.init();
        if (cancelled) {
          detector.close();
          return;
        }
        detectorRef.current = detector;

        // Face detector runs in parallel with pose. We don't await it before
        // starting the tick loop — the tick will simply skip face detection
        // until init resolves, so pose data starts flowing immediately.
        const faceDetector = new FaceDetector();
        faceDetector
          .init()
          .then(() => {
            if (cancelled) {
              faceDetector.close();
              return;
            }
            faceDetectorRef.current = faceDetector;
          })
          .catch((e) => {
            console.warn('Face detector init failed', e);
          });

        if (DIAG) {
          // eslint-disable-next-line no-console
          console.log(`[diag] connecting vision WS to ${wsUrl}`);
        }
        const client = new VisionClient(wsUrl, {
          onAnalysisResult: (r) => {
            setRawFast(r);

            // Wave 20.49bu — Retired the client-side depth-K
            // correction. The old code used
            // `depthScale = baselineSHPD / currentSHPD` where SHPD
            // was the pixel distance between shoulder-mid and
            // hip-mid. That quantity is POSE-DEPENDENT, not
            // depth-dependent: when the rider tucks aero and
            // pitches the torso forward, the shoulders project
            // TOWARD the hips in pixel space and SHPD SHRINKS —
            // even though the rider hasn't moved closer to the
            // camera. `curSHPD < baseSHPD` then made `depthScale > 1`
            // → `depthK > 1` → CdA MULTIPLIED UP under every
            // aero tuck. Exact wrong sign. Reported by the rider
            // repeatedly.
            //
            // Real fix: send a POSE-INVARIANT live px_per_m to
            // the server (derived from shoulder WIDTH ÷
            // calibrated shoulder width, see lastLivePxPerMRef).
            // With that, the server's own area_m² conversion
            // handles distance changes correctly and no client-
            // side factor is needed. Values pass through
            // untouched now.
            const correctedCda = r.cda;
            const correctedArea = r.frontal_area_m2;
            const correctedPower = r.drag_power_w;

            // Feed the smoothed CdA into the baseline tracker and smoothers
            const smoothedCda = cdaSmoothRef.current.update(correctedCda) ?? correctedCda;
            const smoothedArea =
              areaSmoothRef.current.update(correctedArea) ??
              correctedArea;
            const smoothedPower =
              powerSmoothRef.current.update(correctedPower) ??
              correctedPower;

            // Track 1.2 — pass the calibration sanity gate down to the
            // baseline tracker so off-axis/low-health frames don't lock the
            // baseline on noisy geometry.
            const baseline = baselineRef.current.update(
              smoothedCda,
              calibrationUsableRef.current,
            );

            // Track 1.1 — power-validated CdA cross-check.
            // We prefer BLE-measured speed when available; otherwise we fall
            // back to the vision pipeline's predicted speed (which is itself
            // a function of measured power and vision CdA). That fallback is
            // a chicken-and-egg loop in theory, but in practice it converges
            // quickly because both CdAs share the same physics constants.
            // When the rider has a wheel speed sensor (real road), the loop
            // breaks cleanly.
            const sensorPower = getSensors?.()?.power_w;
            const sensorSpeedMps = getSensors?.()?.speed_mps;
            const fallbackSpeedMps = r.predicted_speed_kph
              ? r.predicted_speed_kph / 3.6
              : undefined;
            const powerCdaInput =
              sensorPower != null
                ? computePowerCda({
                    powerW: sensorPower,
                    velocityMps: sensorSpeedMps ?? fallbackSpeedMps ?? 0,
                  })
                : { cda: null, velocityMps: 0 as number };
            const smoothedPowerCda =
              powerCdaInput.cda != null
                ? powerCdaSmoothRef.current.update(powerCdaInput.cda) ??
                  powerCdaInput.cda
                : null;
            const agreement = powerVisionAgreement(smoothedCda, smoothedPowerCda);

            // Throttle UI updates
            const now = performance.now();
            if (now - lastUiUpdateRef.current > 66) {
              // ~15 Hz
              lastUiUpdateRef.current = now;
              setSmoothed({
                cda: smoothedCda,
                frontal_area_m2: smoothedArea,
                drag_force_n: r.drag_force_n,
                drag_power_w: smoothedPower,
                cd: (r as any).cd ?? 0.8,
                segments: r.segments,
                predicted_speed_kph: r.predicted_speed_kph,
                power_cda: smoothedPowerCda,
                power_vision_agreement: agreement,
              });
              const currentLockProgress = baselineRef.current.lockProgress();
              setBaselineState({
                cda: baseline.baselineCda,
                delta: baseline.delta,
                deltaPct: baseline.deltaPct,
                bestCda: baseline.bestCda,
                isLocked: baseline.isLocked,
                lockProgress: currentLockProgress,
              });
              // 2026-07 — log baseline lock progress on ≥10% jumps + on
              // the actual lock event, so remote customers can share a
              // console dump and we can see how close they got.
              if (currentLockProgress - lastLoggedBaselineProgressRef.current >= 0.1) {
                lastLoggedBaselineProgressRef.current = currentLockProgress;
                // eslint-disable-next-line no-console
                console.log(
                  `[baseline] progress=${(currentLockProgress * 100).toFixed(0)}% cda=${smoothedCda.toFixed(3)} usable=${calibrationUsableRef.current}`,
                );
              }
              if (baseline.isLocked && !lastBaselineLockLoggedRef.current) {
                lastBaselineLockLoggedRef.current = true;
                // eslint-disable-next-line no-console
                console.log(`[baseline] LOCKED at CdA=${(baseline.baselineCda ?? 0).toFixed(3)}`);
              }
              // Track 2.3 — feed segmenter once per UI-tick. positionRef
              // holds the latest classifier output; timestampSec is just
              // now / 1000 since pipeline frame timing is monotonic enough.
              const tickPos = positionRef.current;
              if (tickPos) {
                // Wave 20.49l — during the hysteresis transition
                // window, drop the sample rather than credit the
                // still-labeled class. Otherwise the improved CdA
                // the rider earns in the new position lands in the
                // old bucket before the label flip catches up.
                const transitioning = isTransitioning();
                const seg = segmenterRef.current.update(
                  tickPos.class,
                  smoothedCda,
                  now / 1000,
                  transitioning,
                );
                // Reset the CdA One-Euro filter on a confirmed flip
                // so the new bucket starts from the current raw
                // sample instead of dragging in old smoothed state.
                if (
                  lastConfirmedClassRef.current != null &&
                  lastConfirmedClassRef.current !== tickPos.class
                ) {
                  cdaSmoothRef.current.reset();
                }
                lastConfirmedClassRef.current = tickPos.class;
                if (seg.ab) setAbComparison(seg.ab);
                if (seg.completedHold) {
                  setPositionHolds(segmenterRef.current.allHolds());
                }
                // Wave 20.44 — push the in-progress hold snapshot so
                // the session guide can render a "recording drops ·
                // 12/30 s" countdown. Only updates when the value
                // actually changed to keep React honest.
                const snap = segmenterRef.current.currentHold(now / 1000);
                setCurrentHoldSnap((prev) => {
                  if (!prev && !snap) return prev;
                  if (
                    prev &&
                    snap &&
                    prev.positionClass === snap.positionClass &&
                    Math.floor(prev.elapsedSec) === Math.floor(snap.elapsedSec)
                  ) {
                    return prev;
                  }
                  return snap;
                });
              }

              // Track 3.1 — pump the coach state machine. Pure transition
              // detection; any event it emits goes to subscribers via the
              // bus. Done on the UI tick (≤15 Hz) so we don't spin on the
              // hot 30 Hz pipeline path.
              processCoachTick({
                now,
                bus: coachBusRef.current,
                state: coachStateRef.current,
                cda: smoothedCda,
                bestCda: baseline.bestCda,
                baselineCda: baseline.baselineCda,
                isBaselineLocked: baseline.isLocked,
                positionClass: tickPos?.class ?? null,
                calibrationUsable: calibrationUsableRef.current,
                dragPowerW: smoothedPower,
              });
            }
          },
          onRefinement: (r) => {
            setRefinedAnalysis(r);
            setIsRefining(false);
          },
          onKeyframeAck: () => setIsRefining(true),
          onError: (err) => {
            setError(err);
            if (DIAG) {
              // eslint-disable-next-line no-console
              console.log('[diag] vision WS ERROR', err);
            }
          },
          onOpen: () => {
            setError(null);
            if (DIAG) {
              // eslint-disable-next-line no-console
              console.log('[diag] vision WS OPEN');
            }
          },
          onClose: () => {
            if (DIAG) {
              // eslint-disable-next-line no-console
              console.log('[diag] vision WS CLOSED');
            }
          },
        });
        client.connect();
        clientRef.current = client;

        runningRef.current = true;
        rafIdRef.current = requestAnimationFrame(tick);
      } catch (e: any) {
        console.error('Failed to initialize vision pipeline', e);
        setError(e?.message || 'init failed');
      }
    })();

    return () => {
      cancelled = true;
      runningRef.current = false;
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
      clientRef.current?.close();
      clientRef.current = null;
      detectorRef.current?.close();
      detectorRef.current = null;
      faceDetectorRef.current?.close();
      faceDetectorRef.current = null;
      scaleSmoothRef.current.reset();
      cdaSmoothRef.current.reset();
      areaSmoothRef.current.reset();
      powerSmoothRef.current.reset();
      baselineRef.current.reset();
      // Wave 20.49l — hysteresis + segmenter live at module /
      // ref scope; must reset explicitly on pipeline teardown so
      // state doesn't bleed into the next session.
      segmenterRef.current.reset();
      resetHysteresis();
      lastConfirmedClassRef.current = null;
      unusableSinceRef.current = null;
      lastLoggedBaselineProgressRef.current = 0;
      lastBaselineLockLoggedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, wsUrl]);

  // Main per-frame loop
  const tick = useCallback(() => {
    if (!runningRef.current) return;

    const video = videoRef.current;
    const detector = detectorRef.current;
    const client = clientRef.current;

    if (!video || !detector || !client) {
      rafIdRef.current = requestAnimationFrame(tick);
      return;
    }

    if (video.readyState < 2 || video.videoWidth === 0) {
      rafIdRef.current = requestAnimationFrame(tick);
      return;
    }

    const now = performance.now();
    let result: ReturnType<PoseDetector['detect']> = null;

    try {
      result = detector.detect(video, now);
    } catch (e) {
      console.warn('Pose detection failed', e);
    }

    // Face landmarks run in parallel. Skipped until the face detector
    // finishes its async init.
    const faceDetector = faceDetectorRef.current;
    if (faceDetector) {
      try {
        const faceResult = faceDetector.detect(video, now);
        faceLandmarksRef.current = faceResult.landmarks;
      } catch (e) {
        // Don't spam the console — face detect can intermittently fail
        // while a timestamp is still being processed.
      }
    }

    if (result && result.landmarks && result.segmentationMask) {
      landmarksRef.current = result.landmarks;
      worldLandmarksRef.current = result.worldLandmarks;
      maskRef.current = result.segmentationMask;
      const maskW = result.segmentationMask.width;
      const maskH = result.segmentationMask.height;
      maskWidthRef.current = maskW;
      maskHeightRef.current = maskH;

      // Wave 20.49bu — Per-frame px_per_m from pixel shoulder
      // width ÷ calibrated shoulder-width-in-metres. Replaces
      // the old pixel shoulder-hip distance which was
      // pose-dependent (torso pitch shrank it under aero tuck
      // and the pipeline read that as "farther from camera",
      // inflating CdA — exact wrong sign).
      //
      // Shoulders (landmarks 11 + 12) span the body horizontally.
      // On a trainer the rider faces the camera, so the shoulder
      // line stays perpendicular to the camera axis under BOTH
      // pitch (aero tuck) and modest yaw. Its pixel length is
      // therefore driven only by distance-to-camera — the exact
      // quantity we want. Dividing by the rider's calibrated
      // shoulder width (metres) gives px/m, which the server
      // then uses to convert pixel silhouette area into m².
      // Getting closer to the camera scales BOTH pixel area and
      // px/m by the same factor, so area_m² is invariant. Aero
      // tuck (without moving) shrinks pixel area but not px/m,
      // so area_m² correctly SHRINKS — the reported bug is fixed
      // at its source.
      {
        const lm = result.landmarks;
        const lS = lm[11]; const rS = lm[12];
        const goodS =
          (lS?.visibility ?? 0) > 0.5 && (rS?.visibility ?? 0) > 0.5;
        const dims = getSmoothedBodyDimsM();
        if (
          goodS &&
          lS &&
          rS &&
          dims.shoulder_width != null &&
          dims.shoulder_width > 0
        ) {
          const pxSW = Math.hypot(
            (lS.x - rS.x) * maskW,
            (lS.y - rS.y) * maskH,
          );
          if (Number.isFinite(pxSW) && pxSW > 0) {
            lastLivePxPerMRef.current = pxSW / dims.shoulder_width;
          }
        }
      }

      // Wave 7 — derive SMPL fit from world landmarks every frame, but
      // surface to React state at ~15 Hz so the avatar components don't
      // re-render at 30 Hz. bbox_norm is attached after silhouette extraction
      // below — for now the fit has it null and the overlay falls back to
      // px_per_m scaling.
      if (result.worldLandmarks && result.worldLandmarks.length === 33) {
        smplFitRef.current = smplFitterRef.current.fit(result.worldLandmarks, now);
      }
      // Wave 13 / Track H — optional SMPLify-style refinement. Default OFF
      // via env flag; when enabled, applies a tiny silhouette-centroid
      // correction to the SMPL root translation. Perf-gated so a slow
      // device auto-disables after one over-budget run.
      // (refineSmplBySilhouette short-circuits to zero when off — calling
      // it every frame is safe and cheap.)

      // Lazily allocate persistent label + edge buffers on first frame or
      // whenever the mask resolution changes.
      if (!labelMapRef.current || labelMapRef.current.length !== maskW * maskH) {
        labelMapRef.current = new Uint8Array(maskW * maskH);
        edgeMapRef.current = new Uint8Array(maskW * maskH);
      }

      // 1. Run calibration — now uses world landmarks for actual body
      // dimensions instead of population averages.
      const cal = calibrate(
        result.landmarks,
        result.worldLandmarks,
        maskW,
        maskH
      );
      // Smooth the scale (px_per_m) across frames.
      // Wave 20.49bu — Prefer the pose-invariant live px_per_m
      // derived from shoulder width (lastLivePxPerMRef). That's
      // what makes aero tuck actually LOWER reported CdA
      // instead of inflating it — the server uses this to
      // convert pixel area to m² per frame, so both "closer
      // to camera" and "pitching forward" are handled correctly
      // at their source. Falls back to the calibrator's own
      // px_per_m before the live signal is available (early
      // frames, low-visibility, or no calibrated shoulder
      // width yet).
      let smoothedPxPerM = scaleSmoothRef.current.current ?? 0;
      const livePxPerM = lastLivePxPerMRef.current;
      if (livePxPerM != null && Number.isFinite(livePxPerM) && livePxPerM > 0) {
        smoothedPxPerM = scaleSmoothRef.current.update(livePxPerM) ?? 0;
      } else if (cal.confidence > 0.1 && cal.is_usable) {
        smoothedPxPerM = scaleSmoothRef.current.update(cal.px_per_m) ?? 0;
      }
      calibrationUsableRef.current = cal.is_usable;
      setCalibration(cal);

      // 2026-07 — track unusable duration + surface via state (throttled)
      // + log baseline progress transitions so the customer's DevTools
      // console tells us exactly which gate is failing and how close the
      // baseline is to locking.
      if (cal.is_usable) {
        if (unusableSinceRef.current != null) {
          // Just recovered — log it.
          // eslint-disable-next-line no-console
          console.log('[baseline] calibration recovered');
          unusableSinceRef.current = null;
        }
        if (calibrationUnusableForMs != null) setCalibrationUnusableForMs(null);
      } else {
        if (unusableSinceRef.current == null) unusableSinceRef.current = performance.now();
        const elapsed = performance.now() - unusableSinceRef.current;
        // Update the surfaced number in ~500 ms buckets to avoid churn.
        if (
          calibrationUnusableForMs == null ||
          Math.abs(elapsed - calibrationUnusableForMs) > 500
        ) {
          setCalibrationUnusableForMs(elapsed);
        }
      }

      // 2. Classify position
      const pos = classifyPosition(result.landmarks, maskW, maskH);
      setPosition(pos);
      positionRef.current = pos;

      // 3. Compute body segment counts + full mask area (populates labelMap)
      const segments = computeBodySegments(
        result.landmarks,
        result.segmentationMask,
        labelMapRef.current!
      );

      // 4. Extract silhouette contour (populates edgeMap)
      extractContour(result.segmentationMask, edgeMapRef.current!);

      if (segments && smoothedPxPerM > 0) {
        bboxRef.current = segments.bbox;

        // Wave 7 — once we know the silhouette bbox, attach it to the
        // current SMPL fit so AvatarOverlay can align the avatar with the
        // rider in image space. segments.bbox is [x, y, w, h] in mask px;
        // convert to normalized [0..1] for the avatar's coordinate frame.
        const [bx, by, bw, bh] = segments.bbox;
        const bboxNorm: [number, number, number, number] = [
          bx / maskW,
          by / maskH,
          bw / maskW,
          bh / maskH,
        ];
        bboxNormRef.current = bboxNorm;
        if (smplFitRef.current) {
          smplFitRef.current.bbox_norm = bboxNorm;
        }

        // Track H — silhouette-centroid refinement (default OFF). When
        // enabled by env flag AND the perf gate is still open, nudges
        // the SMPL root translation toward the segmentation mask's
        // torso centroid. No-op when flag off or gate closed.
        if (
          smplFitRef.current &&
          smplFitRef.current.root_translation &&
          result.worldLandmarks &&
          labelMapRef.current &&
          cal?.px_per_m
        ) {
          const r = refineSmplBySilhouette({
            jointsWorld: result.worldLandmarks.map((lm) => ({ x: lm.x, y: -lm.y, z: lm.z })),
            labelMap: labelMapRef.current,
            maskW,
            maskH,
            pxPerM: cal.px_per_m,
          });
          if (r.ran) {
            const rt = smplFitRef.current.root_translation;
            smplFitRef.current.root_translation = [
              rt[0] + r.rootDeltaM.x,
              rt[1] + r.rootDeltaM.y,
              rt[2] + r.rootDeltaM.z,
            ];
          }
        }

        // Surface SMPL fit to React state at ~15 Hz to avoid 30 Hz churn in
        // AvatarPanel + AvatarOverlay. Spread into a fresh object so React
        // identity comparison sees a change.
        if (smplFitRef.current && now - lastSmplSurfacedRef.current > 66) {
          lastSmplSurfacedRef.current = now;
          setSmplFit({ ...smplFitRef.current });
        }

        // Diagnostic log every ~2 seconds so you can see the raw numbers
        // in Chrome devtools. Remove once CdA values look correct.
        if (frameCountRef.current % 60 === 1) {
          const rawArea = segments.totalMask / (smoothedPxPerM ** 2);
          console.log(
            `[aero] mask=${maskW}x${maskH} mask_px=${segments.totalMask} ` +
            `px_per_m=${smoothedPxPerM.toFixed(1)} ` +
            `raw_area=${rawArea.toFixed(4)} ` +
            `angle_corr=${cal.angle_correction.toFixed(3)} ` +
            `corrected_area=${(rawArea * cal.angle_correction).toFixed(4)} ` +
            `cal_src=${cal.source} cal_conf=${cal.confidence.toFixed(2)} ` +
            `cal_health=${cal.calibration_health.toFixed(2)} ` +
            `ref_m=${cal.ref_m.toFixed(3)} ` +
            `pos=${pos.class} cd=${pos.cd}`
          );
        }

        // Wave 20.49by — Tier 1 frontal-area correction (fallback).
        // The raw 2D segmentation mask reads the wrong TORSO area
        // when the rider changes pitch. Multiply torso pixels by
        // sin(torsoAngleDeg) so lean-forward correctly reduces area.
        const pitchDeg = pos.torsoAngleDeg ?? 90;
        const pitchFactor = Math.max(
          0.3,
          Math.sin((pitchDeg * Math.PI) / 180),
        );
        const nonTorsoPx = segments.head + segments.arms + segments.legs;
        const correctedTorsoPx = Math.round(segments.torso * pitchFactor);
        const correctedTotalPx = nonTorsoPx + correctedTorsoPx;

        // Wave 20.49bz — Tier 2 frontal-area from a real 3D capsule
        // projection onto the plane perpendicular to rider-forward
        // (MediaPipe +Z here; camera-anchored world landmarks put
        // rider-facing-camera on +Z). Widths come from EMA-smoothed
        // per-rider body dims. When it returns a value, we send it
        // as `frontal_area_m2` directly and let the server skip its
        // pixel→m² conversion (marker field). Null → fall back to
        // the Tier 1 pixel path above so we never make things worse.
        const bodyDims = getSmoothedBodyDimsM();
        const projectedAreaM2 = frontalAreaFromWorldLandmarks(
          result.worldLandmarks ?? null,
          bodyDims,
          { x: 0, y: 0, z: 1 },
        );
        client.sendFrameAnalysis({
          user_id: userId,
          session_id: sessionId,
          timestamp_ms: Date.now(),
          image_dims: [maskW, maskH],
          landmarks: result.landmarks,
          segmentation: {
            frontal_area_px: correctedTotalPx,
            bbox: segments.bbox,
            segment_pixels: {
              head: segments.head,
              torso: correctedTorsoPx,
              arms: segments.arms,
              legs: segments.legs,
            },
            // Wave 20.49bz — projected m² from the 3D capsule body.
            // Present when world landmarks + body dims are good;
            // server prefers this over the pixel-count fallback.
            frontal_area_m2: projectedAreaM2 ?? undefined,
          } as any,
          // New typed calibration payload (replaces old rider_height_* fields)
          calibration: {
            px_per_m: smoothedPxPerM,
            source: cal.source,
            confidence: cal.confidence,
            angle_correction: cal.angle_correction,
          } as any,
          position: {
            class: pos.class,
            confidence: pos.confidence,
            torso_angle_deg: pos.torsoAngleDeg,
            // Track 1.4 — continuous Cd + class probabilities. Server uses
            // cd_blended when present (smooth transitions); older clients
            // omit these fields and the server falls back to the hard class.
            cd_blended: pos.cd,
            probs: pos.classProbs,
          } as any,
          sensors: getSensors?.(),
        });
        if (DIAG) diagFramesSentRef.current += 1;
      } else if (DIAG && smoothedPxPerM <= 0) {
        // The gate that stops frames from being sent. Count for the
        // per-second dump so we can see the ratio of dropped vs sent.
        diagFramesDroppedRef.current += 1;
      }
    }

    // 2026-07 — diag mode per-second dump. Fires only when ?diag=1 (or
    // localStorage flag) is set — zero cost otherwise. Everything a
    // remote customer might want to share about their setup lands in a
    // single [diag] line so a devtools screenshot is enough for us to
    // debug.
    if (DIAG && now - diagLastDumpRef.current > 1000) {
      diagLastDumpRef.current = now;
      const wsOpen = clientRef.current?.isOpen() ? 'open' : 'closed';
      const cal = calibrationUsableRef.current;
      const bp = baselineRef.current.lockProgress();
      // eslint-disable-next-line no-console
      console.log(
        `[diag] ws=${wsOpen} sent=${diagFramesSentRef.current} dropped=${diagFramesDroppedRef.current} ` +
          `cal_usable=${cal} baseline_progress=${(bp * 100).toFixed(0)}%`,
      );
      diagFramesSentRef.current = 0;
      diagFramesDroppedRef.current = 0;
      void diagWsStateRef;
    }

    // FPS counter — update once per second
    frameCountRef.current += 1;
    if (now - lastFpsUpdateRef.current > 1000) {
      setFps(
        Math.round(
          (frameCountRef.current * 1000) / (now - lastFpsUpdateRef.current)
        )
      );
      frameCountRef.current = 0;
      lastFpsUpdateRef.current = now;
    }

    rafIdRef.current = requestAnimationFrame(tick);
  }, [videoRef, userId, sessionId, userHeightM, getSensors]);

  return {
    smoothed,
    rawFast,
    refinedAnalysis,
    isRefining,
    latestLandmarks: landmarksRef.current,
    latestBbox: bboxRef.current,
    calibration,
    position,
    baseline: baselineState,
    fps,
    error,
    visualization: {
      maskRef,
      labelMapRef,
      edgeMapRef,
      maskWidthRef,
      maskHeightRef,
      landmarksRef,
      worldLandmarksRef,
      bboxRef,
      bboxNormRef,
      faceLandmarksRef,
    },
    abComparison,
    positionHolds,
    currentHold: currentHoldSnap,
    finalizePositionSession: (endSec?: number) => {
      // Wave 20.49l — caller can pass an earlier cutoff so the
      // trailing few seconds (e.g., "reach for the End button")
      // don't inflate/deflate the last hold.
      const nowSec = endSec ?? performance.now() / 1000;
      segmenterRef.current.finish(nowSec);
      const fresh = segmenterRef.current.allHolds();
      // Update React state so any UI re-render sees the new
      // count too — but the SYNCHRONOUS return value is what
      // handleEndSession actually reads to build the summary,
      // because setState is async and pipeline.positionHolds
      // would be stale one render longer.
      setPositionHolds(fresh);
      setCurrentHoldSnap(null);
      return fresh;
    },
    resetBaseline: () => {
      baselineRef.current.reset();
      segmenterRef.current.reset();
      cdaSmoothRef.current.reset();
      resetHysteresis();
      lastConfirmedClassRef.current = null;
      lastBaselineLockLoggedRef.current = false;
      setPositionHolds([]);
      setCurrentHoldSnap(null);
      setAbComparison(null);
    },
    coachBus: coachBusRef.current,
    smplFit,
    calibrationUnusableForMs,
  };
}
